import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import '../styles/PublicPlanning.css';

// ---------- Helpers ---------------------------------------------------------

const getDates = (startDate, endDate) => {
  const dates = [];
  const start = new Date(startDate);
  const end   = new Date(endDate);
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1))
    dates.push(d.toISOString().split('T')[0]);
  return dates;
};

const fmtH = (t) => {
  const h = Math.floor(t);
  const m = Math.round((t - h) * 60);
  return `${h}h${m === 0 ? '00' : m.toString().padStart(2, '0')}`;
};

const groupByContent = (acts) => {
  const byLevel = {};
  acts.forEach(a => {
    if (!byLevel[a.level]) byLevel[a.level] = { color: a.levelColor || '#8b5cf6', items: [] };
    byLevel[a.level].items.push(a);
  });
  const groups = {};
  Object.entries(byLevel).forEach(([level, data]) => {
    const sig = data.items.map(a => a.activityType + '::' + a.activity).sort().join('|');
    if (!groups[sig]) {
      groups[sig] = {
        levelsData: [],
        activities: data.items.map(a => ({ name: a.activity, type: a.activityType })),
      };
    }
    groups[sig].levelsData.push({ name: level, color: data.color });
  });
  return Object.values(groups);
};

// ---------- Loading / Error -------------------------------------------------

function LoadingScreen() {
  return (
    <div className="pp-fullscreen">
      <div className="pp-spinner" />
      <p className="pp-loading-text">Chargement du planning...</p>
    </div>
  );
}

function ErrorScreen({ message }) {
  return (
    <div className="pp-fullscreen">
      <div className="pp-error-icon">&#128274;</div>
      <h2 className="pp-error-title">Lien invalide</h2>
      <p className="pp-error-msg">{message}</p>
    </div>
  );
}

// ---------- En-tete style PDF -----------------------------------------------

function PdfHeader({ session, subtitle }) {
  const startFmt = new Date(session.startDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  const endFmt   = new Date(session.endDate).toLocaleDateString('fr-FR',   { day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div className="pp-pdf-header">
      <div className="pp-pdf-header-band">
        <div className="pp-pdf-header-grid-overlay" />
        <div className="pp-pdf-header-logo-col">
          <img
            src={session.logo || '/asterina.png'}
            alt="Logo"
            className="pp-pdf-header-logo"
            onError={e => { e.target.style.display = 'none'; }}
          />
        </div>
        <div className="pp-pdf-header-center">
          <div className="pp-pdf-title-main">PLANNING DE LA SEMAINE</div>
          <div className="pp-pdf-title-sub">FEUILLE DE PROGRAMME</div>
          {session.name && <div className="pp-pdf-session-name">{session.name}</div>}
          {session.location && <div className="pp-pdf-location">{session.location}</div>}
        </div>
        <div className="pp-pdf-header-right">
          <div className="pp-pdf-dates-block">
            <span className="pp-pdf-dates-label">Du {startFmt}</span>
            <span className="pp-pdf-dates-label">au {endFmt}</span>
          </div>
          {subtitle && <div className="pp-pdf-subtitle-right">{subtitle}</div>}
        </div>
      </div>
      <div className="pp-pdf-yellow-stripe" />
      <div className="pp-pdf-readonly-bar">
        <span>Planning public &#x2014; Lecture seule &#x2014; Club Asterina</span>
      </div>
    </div>
  );
}

// ---------- Contenu d'une cellule -------------------------------------------

function CellContent({ groups }) {
  if (!groups || groups.length === 0) {
    return <span className="pp-empty-mark">&#8212;</span>;
  }
  return (
    <div className="pp-groups">
      {groups.map((g, gi) => (
        <div key={gi} className="pp-group" style={{ borderLeftColor: g.levelsData[0].color }}>
          <div className="pp-badges">
            {g.levelsData.map((ld, li) => (
              <span key={li} className="pp-badge" style={{ background: ld.color }}>
                {ld.name}
              </span>
            ))}
          </div>
          <div className="pp-acts">
            {g.activities.map((act, ai) => (
              <span key={ai} className={`pp-act ${act.type === 'th\u00e9orique' ? 'theo' : 'prat'}`}>
                {act.name}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ==========================================================================
// VUE SEMAINE
// ==========================================================================

function WeekView({ session }) {
  const DEFAULT_SLOTS = Array.from({ length: 11 }, (_, i) => i + 8);
  const timeSlots = [...(session.timeSlots || DEFAULT_SLOTS)].sort((a, b) => a - b);
  const dates     = getDates(session.startDate, session.endDate);

  const cellMap = {};
  timeSlots.forEach(hour => {
    cellMap[hour] = {};
    dates.forEach(date => {
      const acts = (session.schedule?.[date]?.activities || []).filter(
        a => Math.abs((a.hour || 0) - hour) < 0.001
      );
      cellMap[hour][date] = groupByContent(acts);
    });
  });

  return (
    <div className="pp-page">
      <PdfHeader session={session} />

      <div className="pp-table-wrapper">
        <div className="pp-table-scroll">
          <table className="pp-table">
            <thead>
              <tr>
                <th className="pp-th pp-th-hour">Heure</th>
                {dates.map(date => (
                  <th key={date} className="pp-th pp-th-day">
                    <span className="pp-th-wday">
                      {new Date(date).toLocaleDateString('fr-FR', { weekday: 'short' })}
                    </span>
                    <span className="pp-th-dnum">{new Date(date).getDate()}</span>
                    <span className="pp-th-mon">
                      {new Date(date).toLocaleDateString('fr-FR', { month: 'short' })}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {timeSlots.map((hour, ri) => (
                <tr key={hour} className={ri % 2 === 0 ? 'pp-row-even' : 'pp-row-odd'}>
                  <td className="pp-td-hour">{fmtH(hour)}</td>
                  {dates.map(date => {
                    const groups     = cellMap[hour][date];
                    const hasContent = groups && groups.length > 0;
                    return (
                      <td key={date} className={`pp-td ${hasContent ? '' : 'pp-td-blank'}`}>
                        <CellContent groups={groups} />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="pp-footer">
        {session.name} &middot; Export&eacute; le {new Date().toLocaleDateString('fr-FR')} &middot; Club Asterina
      </div>
    </div>
  );
}

// ==========================================================================
// VUE JOUR -- uniquement le jour cible
// ==========================================================================

function DayView({ session, date }) {
  const DEFAULT_SLOTS = Array.from({ length: 11 }, (_, i) => i + 8);
  const timeSlots   = [...(session.timeSlots || DEFAULT_SLOTS)].sort((a, b) => a - b);
  const daySchedule = session.schedule?.[date] || { activities: [] };

  const dateLabel = new Date(date).toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  }).replace(/^\w/, c => c.toUpperCase());

  return (
    <div className="pp-page">
      <PdfHeader session={session} subtitle={dateLabel} />

      <div className="pp-day-wrapper">
        <div className="pp-day-title-bar">{dateLabel}</div>
        <div className="pp-table-wrapper pp-day-table-wrapper">
          <table className="pp-table pp-day-table">
            <thead>
              <tr>
                <th className="pp-th pp-th-hour">Heure</th>
                <th className="pp-th pp-th-dayonly">Activit&eacute;s</th>
              </tr>
            </thead>
            <tbody>
              {timeSlots.map((hour, ri) => {
                const acts       = (daySchedule.activities || []).filter(
                  a => Math.abs((a.hour || 0) - hour) < 0.001
                );
                const groups     = groupByContent(acts);
                const hasContent = groups && groups.length > 0;
                return (
                  <tr key={hour} className={ri % 2 === 0 ? 'pp-row-even' : 'pp-row-odd'}>
                    <td className="pp-td-hour">{fmtH(hour)}</td>
                    <td className={`pp-td ${hasContent ? '' : 'pp-td-blank'}`}>
                      <CellContent groups={groups} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {daySchedule.notes && (
          <div className="pp-notes">
            <span className="pp-notes-icon">&#128221;</span>
            <span className="pp-notes-label">Notes du jour</span>
            <p className="pp-notes-text">{daySchedule.notes}</p>
          </div>
        )}
      </div>

      <div className="pp-footer">
        {session.name} &middot; {dateLabel} &middot; Club Asterina
      </div>
    </div>
  );
}

// ==========================================================================
// COMPOSANT PRINCIPAL
// ==========================================================================

function PublicPlanning() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const dayParam = searchParams.get('day');

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = useCallback(() => {
    fetch(`/api/public/${token}`)
      .then(r => {
        if (!r.ok) throw new Error('Lien invalide ou expir\u00e9');
        return r.json();
      })
      .then(setSession)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingScreen />;
  if (error)   return <ErrorScreen message={error} />;
  if (!session) return null;

  const dates    = getDates(session.startDate, session.endDate);
  const validDay = dayParam && dates.includes(dayParam) ? dayParam : null;

  if (validDay) return <DayView session={session} date={validDay} />;
  return <WeekView session={session} />;
}

export default PublicPlanning;
